import {
  booleanAttribute,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { DialogService } from 'src/app/core/services/dialog.service';

@Component({
  selector: 'vee-dialog',
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
  standalone: false,
})
export class DialogComponent implements OnInit, OnDestroy {
  @ViewChild('dialogRef') dialogRef!: ElementRef<HTMLDialogElement>;
  @Input('key') key?: string;
  @Input({ transform: booleanAttribute }) required: boolean = false;
  @Input({ transform: booleanAttribute }) modal: boolean = true;
  @Input() maxWidth:
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | 'full'
    | 'personal-data' = 'md';
  @Output() onOpen = new EventEmitter<any>();
  @Output() onClose = new EventEmitter<any>();

  constructor(
    private renderer: Renderer2,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    if (this.key) {
      this.dialogService.register(this.key, this);
    }
  }

  ngOnDestroy(): void {
    if (this.key) {
      this.dialogService.unregister(this.key);
    }
  }

  @HostListener('click', ['$event'])
  onclick(event: any) {
    let rect = this.dialogRef.nativeElement.getBoundingClientRect();
    const isInDialog =
      rect.top <= event.clientY &&
      event.clientY <= rect.top + rect.height &&
      rect.left <= event.clientX &&
      event.clientX <= rect.left + rect.width;
    if (!isInDialog && !this.required) {
      this._closeDialog();
    }
  }

  _openDialog(event?: any): void {
    // A native <dialog> throws InvalidStateError if showModal()/show() is called
    // while it's already open — e.g. re-opening the cookie dialog with new content
    // (chat-bubble flow) while the initial banner is still showing. The caller's
    // own state change (e.g. `open = true`) already happened; Angular's normal
    // @if bindings reflect it without needing to touch the native element again.
    if (!this.dialogRef.nativeElement.open) {
      if (this.modal) {
        this.dialogRef.nativeElement.showModal();
      } else {
        this.dialogRef.nativeElement.show();
      }
      // Prevent the browser from auto-focusing the first focusable element (e.g. close button),
      // which can show a focus ring on initial load. Keep focus on the dialog container instead.
      queueMicrotask(() => this.dialogRef?.nativeElement?.focus());
      this.toggleBodyScroll(true);
    }
    this.onOpen.emit(event);
  }

  _closeDialog(event?: any): void {
    this.dialogRef.nativeElement.close();
    this.toggleBodyScroll(false);
    this.onClose.emit(event);
  }

  private toggleBodyScroll(disable: boolean): void {
    this.renderer.setStyle(
      document.body,
      'overflow',
      disable ? 'hidden' : 'auto',
    );
  }
}
